import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import { myMembershipsQuery, useMe } from "../api/queries";
import { PublicShell } from "../design/components/PublicShell";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { Reason } from "../design/components/Reason";
import "../design/components/button.css";
import { useT } from "../i18n/locale";
import { signOutTab, switchTab } from "../session/switch-tab";
import "./auth.css";
import "./choose-organization.css";

// «Scegli l'azienda» (`/scegli-azienda`): la scheda sceglie in quale azienda lavorare. La scelta passa dal
// cambio di azienda (`switchTab`), che svuota la cache dell'azienda precedente e porta alla Home.
export function ChooseOrganizationPage() {
  const t = useT();
  const router = useRouter();
  const me = useMe();
  const { motivo, googleCalendar } = useSearch({ from: "/autenticato/scegli-azienda" });
  const guarded = useRouteContext({ from: "/autenticato/scegli-azienda", select: (context) => context.memberships });
  const memberships = useQuery(myMembershipsQuery).data ?? guarded;
  // Una sola scelta alla volta: il secondo clic durante il cambio non parte.
  const [choosing, setChoosing] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  async function choose(membershipId: string) {
    setChoosing(membershipId);
    try {
      await switchTab(router, { kind: "membership", membershipId });
    } finally {
      setChoosing(null);
    }
  }

  async function signOut() {
    setSigningOut(true);
    try {
      await signOutTab(router);
    } finally {
      setSigningOut(false);
    }
  }

  const busy = choosing !== null || signingOut;

  return (
    <PublicShell>
      <Card className="g-stack" aria-busy={busy}>
        <h1>{t("tab.question")}</h1>
        {motivo === "non-disponibile" ? <Reason tone="warning" role="status">{t("tab.unavailable")}</Reason> : null}
        {motivo === "indirizzo" ? <Reason tone="warning" role="status">{t("tab.unknownAddress")}</Reason> : null}
        {googleCalendar === "connected" ? <Reason role="status">{t("appointments.account.justConnected")}</Reason> : null}
        {googleCalendar === "failed" ? <Reason tone="warning" role="status">{t("appointments.account.justFailed")}</Reason> : null}
        <p className="g-auth__muted">{t("tab.onePerTab")}</p>
        {memberships.length > 0 ? (
          <ul className="g-choose__list">
            {memberships.map((membership) => (
              <li key={membership.membershipId}>
                <button type="button" className="g-choose__option" disabled={busy} onClick={() => void choose(membership.membershipId)}>
                  <span className="g-choose__name">{membership.organizationName}</span>
                  <span className="g-choose__role">{membership.roleName}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {me.isPlatformAdmin ? (
          <div className="g-auth__actions">
            <Link to="/gioia365/aziende" className="g-button" data-variant="secondary">{t("tab.platformLink")}</Link>
          </div>
        ) : null}
        {memberships.length === 0 && !me.isPlatformAdmin ? <p>{t("tab.noCompany")}</p> : null}
        <div className="g-auth__actions">
          <Button variant="ghost" onClick={() => void signOut()} disabled={busy}>
            {signingOut ? t("common.signingOut") : t("common.signOut")}
          </Button>
        </div>
      </Card>
    </PublicShell>
  );
}
